import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const HeroSection = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Countdown timer logic - calculates from when user enters the site
  useEffect(() => {
    const targetDate = new Date('2026-01-31T11:00:00').getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = targetDate - now;

      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  const heroContent = {
    ar: {
      eyebrow: "الحدث الطلابي الأبرز في تركيا",
      title: "المؤتمر العام الثاني للاتحاد العام لطلبة سوريا",
      titleBadge: "الدورة الثانية 2026-2027",
      description: "محطةٌ مفصلية لترسيخ العمل المؤسسي، نُتوج فيها ختام الدورة الأولى، ونعلن انطلاق الدورة الثانية؛ لنجدد العهد، ونبني على مكتسبات السابقين، ونرسم برؤية متجددة ملامح المرحلة القادمة.",
      ctaPrimary: "تعرف على أجندة المؤتمر",
      infoBar: "31 يناير - 01 فبراير 2026 | جامعة إسطنبول غيليشم"
    }
  };

  const scrollToAgenda = () => {
    const element = document.getElementById('event-program');
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const content = heroContent?.ar;

  return (
    <section id="hero" className="relative min-h-screen flex items-start justify-center overflow-hidden pt-40 md:pt-48">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="https://res.cloudinary.com/dzpwww3tl/video/upload/v1769709039/%D8%A8%D8%B1%D9%88%D9%85%D9%88_%D9%85%D8%A4%D8%AA%D9%85%D8%B1_%D8%A7%D9%84%D8%A7%D8%AA%D8%AD%D8%A7%D8%AF_fzzhlv.mp4" type="video/mp4" />
        </video>
        {/* Overlay for readability */}
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="space-y-6"
        >
          {/* Eyebrow Text */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-body text-base md:text-lg text-accent italic mb-3"
          >
            {content?.eyebrow}
          </motion.p>

          {/* Main Title */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col items-center gap-4"
            >
              <h1 className="font-memorial text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight text-center">
                <div>المؤتمر العام الثاني</div>
                <div>للاتحاد العام لطلبة سوريا</div>
              </h1>
              <span className="inline-flex items-center px-3 py-1.5 bg-accent/20 border border-accent rounded-lg text-accent font-body text-xs md:text-sm font-medium">
                {content?.titleBadge}
              </span>
            </motion.div>

            {/* Info Bar */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col items-center justify-center gap-4"
            >
              <div className="inline-flex items-center gap-3 bg-card/90 backdrop-blur-sm border border-border rounded-lg px-6 py-3 shadow-memorial">
                <Icon name="Calendar" size={18} className="text-white" />
                <span className="font-body text-sm md:text-base font-medium text-white">
                  {content?.infoBar.split('|')[0].trim()}
                </span>
                <span className="text-white/50">|</span>
                <Icon name="MapPin" size={18} className="text-white" />
                <span className="font-body text-sm md:text-base font-medium text-white">
                  {content?.infoBar.split('|')[1].trim()}
                </span>
              </div>
            </motion.div>

            {/* Countdown Timer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex items-center justify-center gap-4 md:gap-6"
            >
              <div className="grid grid-cols-4 gap-2 md:gap-3">
                {[
                  { value: timeLeft.days, label: 'أيام' },
                  { value: timeLeft.hours, label: 'ساعات' },
                  { value: timeLeft.minutes, label: 'دقائق' },
                  { value: timeLeft.seconds, label: 'ثواني' }
                ].map((item, index) => (
                  <div key={index} className="bg-card/90 backdrop-blur-sm border border-border rounded-lg px-3 py-2 md:px-4 md:py-3 shadow-memorial text-center min-w-[60px] md:min-w-[75px]">
                    <div className="font-memorial text-xl md:text-2xl font-bold text-white mb-1">
                      {String(item.value).padStart(2, '0')}
                    </div>
                    <div className="font-body text-xs md:text-sm text-white">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="font-body text-base md:text-lg text-gray-200 max-w-2xl mx-auto leading-relaxed"
          >
            {content?.description}
          </motion.p>

          {/* Primary CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="flex items-center justify-center pt-6 pb-16"
          >
            <Button
              variant="default"
              size="lg"
              onClick={scrollToAgenda}
              className="w-full sm:w-auto hero-cta-button"
            >
              {content?.ctaPrimary}
            </Button>
          </motion.div>
        </motion.div>

      </div>
    </section>
  );
};

export default HeroSection;